// @ts-nocheck
/**
 * CCMS Realtime (Presence) — Documented Stub Showcase
 *
 * This page DOCUMENTS each component exported from
 * `@/components/ccms-components/realtime` and renders a component live ONLY when
 * it is 100% safe and self-contained (no SDK / network / presence hook on mount,
 * and every required prop can be satisfied with inline mock data).
 *
 * Most realtime components bind to the Presence WebSocket hub via the
 * `usePresenceManager()` store hook. They read live presence state (online users,
 * idle countdown, admin stats, heartbeat config) that is only populated once the
 * hub is connected. Those are shown as DOCS ONLY so this page never crashes at
 * import or render.
 */

import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Code,
  Divider,
  Alert,
  ThemeIcon,
  SimpleGrid,
} from '@/components/ui';
import {
  IconBroadcast,
  IconActivity,
  IconInfoCircle,
  IconPlugConnected,
  IconShieldLock,
} from '@tabler/icons-react';

// All four components + the local hook are re-exported from the realtime barrel.
import {
  PresenceIndicator,
  IdleStatusBadge,
  AutomaticFeaturesPanel,
  PresenceAdminDashboard,
  usePresenceStatus,
} from '@/components/ccms-components/realtime';

/* ------------------------------------------------------------------ */
/* Small helpers for consistent doc-card layout                        */
/* ------------------------------------------------------------------ */

function PropLine({ name, type, required }: { name: string; type: string; required?: boolean }) {
  return (
    <Text size="sm">
      <Code>{name}</Code> — <Text span c="dimmed">{type}</Text> —{' '}
      <Text span c={required ? 'red' : 'dimmed'} fw={required ? 600 : 400}>
        {required ? 'required' : 'optional'}
      </Text>
    </Text>
  );
}

function NeedsHubAlert({ reason }: { reason: string }) {
  return (
    <Alert
      icon={<IconPlugConnected size={16} />}
      color="orange"
      variant="light"
      title="Needs the Presence WebSocket hub to run live"
    >
      <Text size="sm">{reason}</Text>
    </Alert>
  );
}

function LivePreview({ children }: { children: React.ReactNode }) {
  return (
    <Card withBorder radius="md" bg="var(--mantine-color-default-hover)">
      <Stack gap="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" radius="sm" variant="light" color="green">
            <IconActivity size={14} />
          </ThemeIcon>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Live preview
          </Text>
        </Group>
        {children}
      </Stack>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function CcmsRealtimePage() {
  // usePresenceStatus is a LOCAL-state-only hook (useState/useCallback). It does
  // NOT touch the SDK or the presence hub, so it is safe to call here to drive an
  // interactive PresenceIndicator preview.
  const { status, isOnline } = usePresenceStatus({ initialStatus: 'Online' });

  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="xs">
        <Group gap="sm">
          <ThemeIcon size="xl" radius="md" variant="light" color="blue">
            <IconBroadcast size={26} />
          </ThemeIcon>
          <Title order={2}>CCMS Realtime (Presence)</Title>
        </Group>
        <Text c="dimmed" maw={760}>
          Realtime presence components from{' '}
          <Code>@/components/ccms-components/realtime</Code>. Most of these bind to the{' '}
          <strong>Presence WebSocket hub</strong> (via the <Code>usePresenceManager()</Code>{' '}
          store) and read live state such as online users, idle countdown, heartbeat config
          and admin statistics. This page documents every export and renders a component live
          only when it is fully self-contained with mock data.
        </Text>
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            Components that call <Code>usePresenceManager()</Code> on mount are shown as{' '}
            <strong>docs only</strong> here — without a connected hub their backing store is
            empty, so a standalone live preview would be misleading (and several render{' '}
            <Code>null</Code> until the hub provides data).
          </Text>
        </Alert>
      </Stack>

      <Divider />

      {/* ============================================================ */}
      {/* PresenceIndicator — SAFE, rendered live                       */}
      {/* ============================================================ */}
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>PresenceIndicator</Title>
              <Text size="sm" c="dimmed">
                A colored status dot (+ optional label) for a user's presence state.
              </Text>
            </Stack>
            <Badge color="green" variant="light">
              Live preview
            </Badge>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Key props
            </Text>
            <PropLine name="userId" type="string" />
            <PropLine name="status" type="'Online' | 'Away' | 'Busy' | 'Offline'" />
            <PropLine name="showStatus" type="boolean (default true)" />
            <PropLine name="size" type="'sm' | 'md' | 'lg' (default 'md')" />
          </Stack>

          <Code block language="tsx">{`import { PresenceIndicator } from '@/components/ccms-components/realtime';

<PresenceIndicator status="Online" />
<PresenceIndicator status="Away" size="lg" />
<PresenceIndicator status="Busy" showStatus={false} />`}</Code>

          <Badge color="teal" variant="light" leftSection={<IconActivity size={12} />}>
            Safe: takes an explicit status, no hub required
          </Badge>

          <LivePreview>
            <Group gap="xl">
              <PresenceIndicator status="Online" />
              <PresenceIndicator status="Away" />
              <PresenceIndicator status="Busy" />
              <PresenceIndicator status="Offline" />
            </Group>
            <Divider my="xs" label="sizes" labelPosition="center" />
            <Group gap="xl" align="center">
              <PresenceIndicator status="Online" size="sm" />
              <PresenceIndicator status="Online" size="md" />
              <PresenceIndicator status="Online" size="lg" />
            </Group>
          </LivePreview>
        </Stack>
      </Card>

      {/* ============================================================ */}
      {/* usePresenceStatus — SAFE local hook, demonstrated live        */}
      {/* ============================================================ */}
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>usePresenceStatus (hook)</Title>
              <Text size="sm" c="dimmed">
                Local React state helper for tracking a presence status value. The real SDK
                wiring (<Code>presenceClient.updateStatus(...)</Code>) is stubbed out, so it is
                safe to use standalone.
              </Text>
            </Stack>
            <Badge color="green" variant="light">
              Live preview
            </Badge>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Options / return
            </Text>
            <PropLine name="options.initialStatus" type="PresenceStatus (default 'Offline')" />
            <PropLine name="options.onStatusChange" type="(status) => void" />
            <PropLine name="returns.status" type="PresenceStatus" required />
            <PropLine name="returns.setStatus" type="(status) => void" required />
            <PropLine name="returns.isOnline" type="boolean (Online | Busy)" required />
          </Stack>

          <Code block language="tsx">{`import {
  usePresenceStatus,
  PresenceIndicator,
} from '@/components/ccms-components/realtime';

const { status, setStatus, isOnline } = usePresenceStatus({
  initialStatus: 'Online',
  onStatusChange: (s) => console.log('changed:', s),
});

<PresenceIndicator status={status} />
<Button onClick={() => setStatus('Away')}>Set Away</Button>`}</Code>

          <Badge color="teal" variant="light" leftSection={<IconActivity size={12} />}>
            Safe: useState-only, no hub required
          </Badge>

          <LivePreview>
            <Group gap="md" align="center">
              <PresenceIndicator status={status} size="lg" />
              <Text size="sm" c="dimmed">
                hook value: <Code>{status}</Code> · isOnline:{' '}
                <Code>{String(isOnline)}</Code>
              </Text>
            </Group>
          </LivePreview>
        </Stack>
      </Card>

      <Divider label="Hub-bound components (docs only)" labelPosition="center" />

      {/* ============================================================ */}
      {/* IdleStatusBadge — DOC ONLY (usePresenceManager)               */}
      {/* ============================================================ */}
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>IdleStatusBadge</Title>
              <Text size="sm" c="dimmed">
                Shows a "💤 Idle" badge with a countdown once the user goes idle. Designed to
                sit in the header near the profile menu.
              </Text>
            </Stack>
            <Badge color="gray" variant="light">
              Docs only
            </Badge>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Key props
            </Text>
            <PropLine name="tooltipMessage" type="string" />
            <PropLine name="size" type="'xs' | 'sm' | 'md' | 'lg' (default 'sm')" />
            <PropLine name="color" type="string (default 'orange')" />
            <PropLine name="showTimeRemaining" type="boolean (default false)" />
          </Stack>

          <Code block language="tsx">{`import { IdleStatusBadge } from '@/components/ccms-components/realtime';

<IdleStatusBadge />
<IdleStatusBadge showTimeRemaining />`}</Code>

          <NeedsHubAlert reason="Calls usePresenceManager() on mount and reads isIdle / formattedIdleTime from the presence store. It renders null until the hub reports the user is idle, so it has no standalone preview." />
        </Stack>
      </Card>

      {/* ============================================================ */}
      {/* AutomaticFeaturesPanel — DOC ONLY (usePresenceManager)        */}
      {/* ============================================================ */}
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>AutomaticFeaturesPanel</Title>
              <Text size="sm" c="dimmed">
                Settings panel for the presence manager: toggle auto-heartbeat, activity
                tracking and idle detection, and tune their intervals.
              </Text>
            </Stack>
            <Badge color="gray" variant="light">
              Docs only
            </Badge>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Key props
            </Text>
            <PropLine name="title" type="string (default 'Automatic Features')" />
            <PropLine name="withBorder" type="boolean (default true)" />
            <PropLine name="className" type="string" />
          </Stack>

          <Code block language="tsx">{`import { AutomaticFeaturesPanel } from '@/components/ccms-components/realtime';

<AutomaticFeaturesPanel />
<AutomaticFeaturesPanel title="Presence Settings" />`}</Code>

          <NeedsHubAlert reason="Calls usePresenceManager() on mount and binds its switches/inputs to config and updateConfig from the presence store. Without an initialized manager the config is unavailable, so it is not previewed live." />
        </Stack>
      </Card>

      {/* ============================================================ */}
      {/* PresenceAdminDashboard — DOC ONLY (usePresenceManager, Admin) */}
      {/* ============================================================ */}
      <Card withBorder radius="md" padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>PresenceAdminDashboard</Title>
              <Text size="sm" c="dimmed">
                Admin-only stat board: total / online / away / busy / offline counts plus
                actions like "Set All Offline".
              </Text>
            </Stack>
            <Group gap="xs">
              <Badge color="red" variant="light" leftSection={<IconShieldLock size={12} />}>
                Admin
              </Badge>
              <Badge color="gray" variant="light">
                Docs only
              </Badge>
            </Group>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Key props
            </Text>
            <PropLine name="title" type="string (default 'Presence Admin Dashboard')" />
            <PropLine name="withBorder" type="boolean (default true)" />
            <PropLine name="className" type="string" />
            <PropLine name="hideActions" type="boolean (default false)" />
          </Stack>

          <Code block language="tsx">{`import { PresenceAdminDashboard } from '@/components/ccms-components/realtime';

<PresenceAdminDashboard />
<PresenceAdminDashboard hideActions />`}</Code>

          <NeedsHubAlert reason="Calls usePresenceManager() on mount, requires an Admin JWT (isAdmin) and presenceStats from the hub, and fires manager.setAllUsersOffline(). It renders null when not admin / no stats, so there is no safe standalone preview." />
        </Stack>
      </Card>

      {/* Footer note */}
      <Divider />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Alert icon={<IconActivity size={16} />} color="green" variant="light" title="Rendered live">
          <Text size="sm">
            <Code>PresenceIndicator</Code> and the <Code>usePresenceStatus</Code> hook —
            self-contained, no hub required.
          </Text>
        </Alert>
        <Alert icon={<IconPlugConnected size={16} />} color="orange" variant="light" title="Docs only (hub-bound)">
          <Text size="sm">
            <Code>IdleStatusBadge</Code>, <Code>AutomaticFeaturesPanel</Code> and{' '}
            <Code>PresenceAdminDashboard</Code> — each calls{' '}
            <Code>usePresenceManager()</Code> and needs the Presence WebSocket hub.
          </Text>
        </Alert>
      </SimpleGrid>
    </Stack>
  );
}
